import { Button, Item, ItemActions, ItemContent, ItemHeader, ItemTitle } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ROOT_STRUCT_CODE } from '@/entities/attributes';
import { useAttributes } from '@/entities/attributes/model/api/hooks';
import { AttributeFormValues } from '../validation';
import { NestedAttributeSelector } from './NestedAttributeSelector';
import { StructureAttributeItem } from './StructureAttributeItem';

type Props = {
  disabled?: boolean;
};

export const StructureAttributesList: FC<Props> = ({ disabled }) => {
  const { t } = useTranslation(['attributes', 'common']);
  const { control } = useFormContext<AttributeFormValues>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'structure.attributes',
  });

  // Fetch available attributes for nesting (parent-struct-type=SIMPLE)
  const { data: availableAttributesData } = useAttributes({ 'parent-struct-code': ROOT_STRUCT_CODE, limit: 1000 });
  const availableAttributes = useMemo(() => availableAttributesData?.attributes ?? [], [availableAttributesData]);

  const handleAddNew = () => {
    append({
      code: '',
      name: '',
      // Тип данных нового вложенного атрибута не предзаполняется: выбор делает пользователь,
      // без выбора форма не сохраняется — как и в карточке отдельного атрибута
      dataType: null,
      isList: false,
      description: '',
      type: null,
      rule: null,
      tooltip: '',
      isNew: true,
    });
  };

  const handleAddExisting = (attributeCode: string) => {
    const attribute = availableAttributes.find((a) => a.code === attributeCode);
    if (attribute) {
      append({
        code: attribute.code,
        name: attribute.name,
        dataType: attribute.dataType,
        isList: attribute.isList ?? false,
        description: attribute.description,
        type: attribute.type ?? null,
        rule: attribute.rule ?? null,
        tooltip: attribute.tooltip ?? '',
        isNew: false,
      });
    }
  };

  return (
    <Item variant="muted" className="w-full">
      <ItemHeader>
        <ItemTitle>{t('attributes:structureAttributes')}</ItemTitle>
        <ItemActions className="flex-wrap justify-end gap-2">
          {!disabled && (
            <>
              <NestedAttributeSelector
                availableAttributes={availableAttributes}
                onSelect={handleAddExisting}
                trigger={
                  <Button variant="secondary" size="sm">
                    {t('attributes:addExistingAttribute')}
                  </Button>
                }
              />
              <Button variant="secondary" size="sm" onClick={handleAddNew}>
                {t('attributes:addNewAttribute')}
              </Button>
            </>
          )}
        </ItemActions>
      </ItemHeader>
      <ItemContent>
        <div className="flex flex-col gap-3">
          {fields.map((field, index) => (
            <StructureAttributeItem key={field.id} index={index} disabled={disabled} onRemove={() => remove(index)} />
          ))}
          {fields.length === 0 && (
            <p className="text-sm text-muted-foreground">{t('attributes:noAttributesInStructure')}</p>
          )}
        </div>
      </ItemContent>
    </Item>
  );
};
